import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { Typography, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import TopBar from 'components/TopBar';
import TransitionScreen from 'components/shared/TransitionScreen';
import Stack from 'components/shared/Stack';

import { apiChangeAvailability } from 'state/common.actions';
import store, { RootState } from 'state/store';
import { browserHistory } from 'utils/functions';
import { ConnectionState } from 'typings';
import { apiFetchAssignedLead } from 'state/concierge.state';

const SplashContent = styled('div')`
  margin-top: auto;
  text-align: center;

  .splash-text {
    font-weight: bold;
  }

  .splash-action {
    padding-left: ${(props) => props.theme.spacing(5)};
    padding-right: ${(props) => props.theme.spacing(5)};
    text-transform: none;
  }
`;

const ConciergeTransitionScreen = () => {
  const dispatch = useDispatch();

  const { isConnected } = useSelector(
    (state: RootState) => ({
      isConnected: state.app.connectionState === ConnectionState.CONNECTED,
    }),
    shallowEqual
  );

  const { conciergeIsActive, assignedLeadId, completedOneLead, isAwaitingNewLead, isSubscribedToLdeBroadcastChannel } = useSelector(
    (state: RootState) => ({
      conciergeIsActive: state.concierge.isActive,
      assignedLeadId: state.concierge.assignedLeadId,
      completedOneLead: state.concierge.completedOneLead,
      isAwaitingNewLead: state.concierge.isAwaitingNewLead,
      isSubscribedToLdeBroadcastChannel: state.concierge.isSubscribedToLdeBroadcastChannel,
    }),
    shallowEqual
  );
  const theme = useTheme();

  const canReceiveLdeBroadcastEvents = isConnected && isSubscribedToLdeBroadcastChannel;

  // clear out old lead when welcome screen is rendered
  useEffect(() => {
    store.dispatch({ type: 'RESET_LEAD' });
  }, []);

  useEffect(() => {
    // exit early if this isn't an initial console load or they don't have a lead
    if (isAwaitingNewLead || !assignedLeadId) return;

    // redirect to the assigned lead
    browserHistory.push(`/${assignedLeadId}`);
  }, [assignedLeadId, isAwaitingNewLead]);

  useEffect(() => {
    // exit early if this isn't an initial console load, concierge is inactive, or they already have a lead
    if (isAwaitingNewLead || assignedLeadId || !conciergeIsActive) return;

    // exit early if we aren't able to receive LDE broadcast events;
    // let's wait so we can cover the time we were disconnected and don't make the request too frequently
    if (!canReceiveLdeBroadcastEvents) return;

    // fetch the concierge's assigned lead once again;
    // this makes sure that any gap between fetching the concierge and ability to receive LDE events is covered
    dispatch(apiFetchAssignedLead());
  }, [assignedLeadId, isAwaitingNewLead, conciergeIsActive, canReceiveLdeBroadcastEvents]);

  const handleBtnClick = () => {
    dispatch(apiChangeAvailability({ isActive: true }));
  };

  return (
    <TransitionScreen>
      <TopBar />
      <SplashContent>
        {conciergeIsActive ? (
          <>
            {completedOneLead && (
              <Typography className="splash-text" variant="h1" color="primary">
                Nice Work!
              </Typography>
            )}
            <Typography variant="h4" color="primary">
              Hang tight while we fetch a new lead.
            </Typography>
          </>
        ) : (
          <Stack space={theme.spacing(4)}>
            <div>
              <Typography className="splash-text" variant="h1" color="primary">
                Welcome Back!
              </Typography>
              <Typography variant="h4" color="primary">
                Click below when you’re ready for a lead
              </Typography>
            </div>
            <div>
              <Button
                type="button"
                className="splash-action"
                variant="contained"
                color="primary"
                onClick={handleBtnClick}
              >
                <Typography className="splash-text" variant="h2">
                  {"Let's Go"}
                </Typography>
              </Button>
            </div>
          </Stack>
        )}
      </SplashContent>
    </TransitionScreen>
  );
};

export default ConciergeTransitionScreen;
